import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import FullWidthTabs from 'shared/atoms/FullWidthTabs';

import { AddQuestionsCsv, EditTest } from './tabs';
import { parseCsvContent } from './utils';

const ManageTest = ({
  alwaysExpanded,
  defaultTestQuestions,
  disabled,
  isEditing,
  languages,
  pastedCsvContent,
  selectedLanguage,
  setErrorMessage,
  setHasModified,
  setPastedCsvContent,
  setTestQuestions,
  testQuestions,
  theme: themeLayout,
}) => {
  const { t } = useTranslation();

  const [selectedTabIndex, setSelectedTabIndex] = useState(isEditing ? 1 : 0);

  const onChangeTabHandler = (index) => {
    if (isEditing) {
      setSelectedTabIndex(index);
    } else {
      const { testQuestions, error } = parseCsvContent(
        pastedCsvContent,
        t,
        languages[0]
      );
      if (setErrorMessage) setErrorMessage(error);

      if (testQuestions.length > 0 && selectedTabIndex === 0) {
        setTestQuestions(testQuestions);
        setSelectedTabIndex(1);
      } else {
        setSelectedTabIndex(0);
      }
    }
  };

  useEffect(() => {
    setSelectedTabIndex(isEditing ? 1 : 0);
  }, [isEditing]);

  useEffect(() => {
    const testWasDeleted =
      pastedCsvContent === '' &&
      testQuestions.length === 0 &&
      selectedTabIndex !== 0;
    if (testWasDeleted) {
      setSelectedTabIndex(0);
    }
  }, [pastedCsvContent]);

  const labels = { tab1: t('quizzes.newTest'), tab2: t('quizzes.editTest') };

  const tabsToSelect = [
    {
      label: labels.tab1,
      component: (
        <AddQuestionsCsv
          disabled={disabled}
          pastedCsvContent={pastedCsvContent}
          setPastedCsvContent={(content) => {
            setPastedCsvContent(content);
            const { testQuestions, error } = parseCsvContent(
              content,
              t,
              languages[0]
            );

            if (error) {
              setErrorMessage(error);
            } else {
              setTestQuestions(testQuestions);
              setSelectedTabIndex(1);
            }
            if (setHasModified)
              setHasModified(
                JSON.stringify(defaultTestQuestions) !==
                  JSON.stringify(testQuestions)
              );
          }}
          setTestQuestions={setTestQuestions}
          theme={themeLayout}
        />
      ),
      disabled: isEditing,
    },
    {
      label: labels.tab2,
      component: (
        <EditTest
          disabled={disabled}
          isEditing={isEditing}
          alwaysExpanded={alwaysExpanded}
          questions={testQuestions}
          selectedLanguage={selectedLanguage}
          setQuestions={(questions) => {
            if (setHasModified)
              setHasModified(
                JSON.stringify(defaultTestQuestions) !==
                  JSON.stringify(questions)
              );
            setTestQuestions(questions);
          }}
          theme={themeLayout}
        />
      ),
      disabled: !isEditing && pastedCsvContent.length === 0,
    },
  ];

  return (
    <FullWidthTabs
      tabsToSelect={tabsToSelect}
      onChangeTabHandler={onChangeTabHandler}
      selectedTabIndex={selectedTabIndex}
      theme={themeLayout}
    />
  );
};

ManageTest.propTypes = {
  alwaysExpanded: PropTypes.bool,
  defaultTestQuestions: PropTypes.array,
  disabled: PropTypes.bool,
  isEditing: PropTypes.bool.isRequired,
  languages: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedLanguage: PropTypes.string.isRequired,
  pastedCsvContent: PropTypes.string.isRequired,
  setErrorMessage: PropTypes.func,
  setHasModified: PropTypes.func,
  setPastedCsvContent: PropTypes.func.isRequired,
  setTestQuestions: PropTypes.func.isRequired,
  testQuestions: PropTypes.array.isRequired,
  theme: PropTypes.string,
};

ManageTest.defaultProps = {
  theme: 'light',
};

export default ManageTest;
